import React, { useState, useEffect } from 'react';
import QuizQuestionCard from './QuizQuestionCard';
import MathText from './ui/MathText';
import { ClickToViewImage } from './ImageViewer';
import CloseButton from './ui/CloseButton';
import SourceDocViewer from './SourceDocViewer';
import { TC_BLANK_LABELS } from '../utils/Constants';

// "Preview Question In Quiz" modal. Given the raw form fields, it assembles the same question
// object the quiz API returns and renders it with QuizQuestionCard — the exact component the live
// quiz uses — so admins can verify math, options, images, passages, TC blanks and SE checkboxes
// before saving. Interactive (clickable) but not scored; the favorite star is hidden. Below the
// card it shows the correct answer + a collapsible explanation (styled like Quiz Submission
// Details). Optional Next/Prev navigate the underlying editor in lockstep.
//
// Props: open, onClose, fields, and (optional) onPrev, onNext, hasPrev, hasNext, serialLabel.
// `fields` = {
//   questionText, questionType, correctOptions (['A','C']), options ({A:'',..,H:''}),
//   optionImages ({A:'',..,H:''}), questionImageUrl, blanks ([{choices, correct}]),
//   textAnswer, sideText, audioUrl,
//   correctTextAnswer, answerExplanation, explanationImageUrl }
export function buildPreviewQuestion(fields) {
    const {
        questionText = '', questionType = 'MCQ', correctOptions = [], options = {},
        optionImages = {}, questionImageUrl = '', blanks = [], sideText = '', audioUrl = '',
    } = fields || {};
    const q = {
        question_id: 'preview',
        question_type: questionType,
        question_text: questionText,
        question_image_url: questionImageUrl,
        // SE is always multi-select; MCQ is multi when more than one correct option is ticked.
        multiple_correct: questionType === 'SENTENCE_EQUIVALENCE' || (questionType === 'MCQ' && (correctOptions || []).length > 1),
        side_text: sideText,
        audio_url: audioUrl,
    };
    ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].forEach(l => {
        q[`option_${l.toLowerCase()}`] = options[l] || '';
        q[`option_${l.toLowerCase()}_image_url`] = optionImages[l] || '';
    });
    if (questionType === 'TEXT_COMPLETION') {
        q.metadata = { blanks: (blanks || []).map(b => ({ choices: [...(b.choices || [])], correct: b.correct })) };
    }
    return q;
}

// Read-only "correct answer" block, matching the green styling used in Quiz Submission Details.
function AnswerReveal({ fields }) {
    const { questionType = 'MCQ', correctOptions = [], correctTextAnswer = '', blanks = [], options = {} } = fields || {};
    const green = { backgroundColor: '#d1fae5', color: '#065f46', padding: '0.5rem', borderRadius: '0.25rem' };

    if (questionType === 'TEXT') {
        return (
            <div style={green}>
                <strong>Correct Answer: </strong>
                {correctTextAnswer ? <MathText text={correctTextAnswer} /> : <span className="text-muted">— not set —</span>}
            </div>
        );
    }
    if (questionType === 'TEXT_COMPLETION') {
        return (
            <div className="d-flex flex-column gap-1">
                {(blanks || []).map((b, bi) => {
                    const ci = b.correct;
                    const choice = (b.choices || [])[ci];
                    return (
                        <div key={bi} style={green}>
                            <strong>Blank {TC_BLANK_LABELS[bi] || `#${bi + 1}`}: </strong>
                            {choice != null && choice !== '' ? <MathText text={choice} /> : <span className="text-muted">— not set —</span>}
                        </div>
                    );
                })}
            </div>
        );
    }
    // MCQ / Sentence Equivalence: the correct option letters (+ their text for context).
    const letters = (correctOptions || []).slice().sort();
    if (letters.length === 0) return <div style={green}><strong>Correct Answer: </strong><span className="text-muted">— not set —</span></div>;
    return (
        <div className="d-flex flex-column gap-1">
            {letters.map(l => (
                <div key={l} style={green}>
                    <strong>{l}. </strong>{options[l] ? <MathText text={options[l]} /> : null}
                </div>
            ))}
        </div>
    );
}

export default function QuestionPreviewModal({ open, onClose, fields, onPrev = null, onNext = null, serialLabel = '', sourceDoc = null, setSourceDoc = null }) {
    // Local, throwaway selection state so the preview feels real without scoring/persistence.
    const [answer, setAnswer] = useState(undefined);
    const [showExplanation, setShowExplanation] = useState(true);
    const [copied, setCopied] = useState(false);

    // Copy the question text + its options as "question A. opt1 B. opt2 …" (only filled options).
    const copyQuestionAndOptions = async () => {
        const opts = (fields && fields.options) || {};
        const optParts = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']
            .filter((l) => (opts[l] || '').trim())
            .map((l) => `${l}. ${opts[l].trim()}`);
        const text = [((fields && fields.questionText) || '').trim(), ...optParts]
            .filter(Boolean)
            .join(' ');
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* clipboard unavailable */
        }
    };

    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // Reset the throwaway selection + explanation toggle whenever the previewed question changes
    // (serialLabel changes on navigation; the question text also changes).
    useEffect(() => {
        setAnswer(undefined);
        setShowExplanation(true);
    }, [serialLabel, fields?.questionText]);

    if (!open) return null;
    const question = buildPreviewQuestion(fields);
    const answerExplanation = fields?.answerExplanation;
    const explanationImageUrl = fields?.explanationImageUrl;

    const onOptionSelect = (_qid, key) => setAnswer(cur => (cur === key ? undefined : key));
    const onMultiToggle = (_qid, key) => setAnswer(cur => {
        const arr = Array.isArray(cur) ? cur : [];
        return arr.includes(key) ? arr.filter(k => k !== key) : [...arr, key];
    });
    const onBlankSelect = (_qid, bi, ci) => setAnswer(cur => {
        const blanks = { ...((cur && cur.blanks) || {}) };
        if (blanks[bi] === ci) delete blanks[bi]; else blanks[bi] = ci;
        return { blanks };
    });
    const onTextChange = (_qid, text) => setAnswer(text ? { text } : undefined);

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, backdropFilter: 'blur(1px)' }}>
            <div style={{ backgroundColor: '#ffffff', borderRadius: '0.9rem', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', width: '92%', maxWidth: setSourceDoc ? 'min(1400px, 97vw)' : 'min(900px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb' }}>
                    <h5 style={{ margin: 0, fontWeight: 700, color: '#1f2937' }}>
                        Preview — how this looks in a test
                        {serialLabel ? <span className="badge bg-light text-secondary ms-2" style={{ fontWeight: 600 }}>{serialLabel}</span> : null}
                    </h5>
                    <CloseButton onClick={onClose} style={{ position: 'static' }} />
                </div>
                {/* Body: question preview (left) + shared source-doc viewer (right, like the
                    background editor) so the admin can match the preview against the source PDF/image. */}
                <div style={{ display: 'flex', gap: '0.75rem', flex: 1, minHeight: 0 }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
                    <div>
                        <button
                            type="button"
                            onClick={copyQuestionAndOptions}
                            style={{ border: '1px solid #7c3aed', color: '#7c3aed', background: 'transparent', borderRadius: '0.375rem', padding: '0.3rem 0.7rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginBottom: '0.75rem' }}
                        >
                            {copied ? 'Copied!' : 'Copy question & options'}
                        </button>
                    </div>
                    <QuizQuestionCard
                        question={question}
                        qIndex={0}
                        includePassage={true}
                        answer={answer}
                        isAnswered={answer !== undefined}
                        onOptionSelect={onOptionSelect}
                        onMultiToggle={onMultiToggle}
                        onBlankSelect={onBlankSelect}
                        onTextChange={onTextChange}
                    />

                    {/* Answer key + explanation (read-only; styled like Quiz Submission Details). */}
                    <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                        <div style={{ fontWeight: 700, color: '#065f46', marginBottom: '0.4rem' }}>Correct answer</div>
                        <AnswerReveal fields={fields} />
                        {(answerExplanation || explanationImageUrl) && (
                            <div className="mt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowExplanation(v => !v)}
                                    style={{ backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '0.375rem', padding: '0.35rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
                                >
                                    {showExplanation ? 'Hide Explanation' : 'Show Explanation'}
                                </button>
                                {showExplanation && (
                                    <div style={{ marginTop: '0.5rem', padding: '0.75rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#374151', whiteSpace: 'pre-wrap' }}>
                                        {answerExplanation && <MathText text={answerExplanation} />}
                                        {explanationImageUrl && (
                                            <ClickToViewImage src={explanationImageUrl} containerStyle={{ marginTop: answerExplanation ? '0.5rem' : 0 }} />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.75rem', textAlign: 'center' }}>Preview only — selections here are not saved or scored.</p>
                </div>
                {/* Prev / Next — anchored to the bottom-right of the question column (not under
                    the source viewer). Only shown when handlers are provided (Edit / Review-Parsed). */}
                {(onPrev || onNext) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb' }}>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => onPrev && onPrev()} disabled={!onPrev}>◀ Previous</button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => onNext && onNext()} disabled={!onNext}>Next ▶</button>
                    </div>
                )}
                </div>
                {setSourceDoc && (
                    <div style={{ flex: '0 0 44%', borderLeft: '1px solid #e5e7eb', padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <SourceDocViewer doc={sourceDoc} onDoc={setSourceDoc} onClose={() => setSourceDoc(null)} />
                    </div>
                )}
                </div>
            </div>
        </div>
    );
}
